import { getDefaultStore } from 'jotai'
import { printTemplatesAtom, userStoreIdAtom } from '../global/AtomsProvider'

/**
 * Get the appropriate print template for a given type and store
 * @param {string} type - Template type ('sale', 'supply', 'supplyOrder')
 * @param {string} storeId - Store ID (optional, if not provided uses current user's store)
 * @returns {Object|null} The selected template or null if not found
 */
export const getPrintTemplate = (type, storeId = null) => {
  try {
    const store = getDefaultStore()
    const currentStoreId = storeId || store.get(userStoreIdAtom)

    // Get templates from atom (real-time updated)
    const templates = store.get(printTemplatesAtom)

    // Filter templates by type and store
    const filteredTemplates = templates.filter(template => {
      if (template.type !== type) return false

      // If storeId is provided, match store-specific or global templates
      if (currentStoreId) {
        return !template.store || template.store === currentStoreId
      }

      // If no storeId, only return global templates
      return !template.store
    })

    if (filteredTemplates.length === 0) {
      console.warn(`No print templates found for type: ${type}, store: ${currentStoreId}`)
      return null
    }

    // Priority order:
    // 1. Store-specific default template
    // 2. Store-specific template (first one)
    // 3. Global default template
    // 4. Global template (first one)

    // Look for store-specific default
    const storeSpecificDefault = filteredTemplates.find(t =>
      t.store === currentStoreId && t.isDefault
    )
    if (storeSpecificDefault) return storeSpecificDefault

    // Look for any store-specific template
    const storeSpecific = filteredTemplates.find(t => t.store === currentStoreId)
    if (storeSpecific) return storeSpecific

    // Look for global default
    const globalDefault = filteredTemplates.find(t =>
      !t.store && t.isDefault
    )
    if (globalDefault) return globalDefault

    // Return first global template
    const globalTemplate = filteredTemplates.find(t => !t.store)
    if (globalTemplate) return globalTemplate

    // Fallback to first available template
    return filteredTemplates[0]

  } catch (error) {
    console.error('Error getting print template:', error)
    return null
  }
}

/**
 * Get print template settings for a given type and store
 * This is the main function that should be used in screens
 * @param {string} type - Template type
 * @param {string} storeId - Store ID (optional)
 * @returns {Object} Template settings object
 */
export const getPrintTemplateSettings = (type, storeId = null) => {
  const template = getPrintTemplate(type, storeId)

  if (template && template.settings) {
    return template.settings
  }

  // Fallback to default settings
  return null
}